import React, { useState, useEffect } from "react";
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
} from "@mui/material";
import api from "../../globals/axios";
import { useTranslation } from "react-i18next";
import GenericButton from "../../components/Custom/GenericButton";
import { useAlert } from "../../components/Alert";
import UserCampaignCard from "../../components/Custom/UserCampaignCard";
import { handleMobileKeyDown } from "../../globals/utils";

const ActiveCampaigns = () => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showAlert } = useAlert();
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);
  const [signupLoading, setSignupLoading] = useState({});

  // New state for signup dialog
  const [openSignupDialog, setOpenSignupDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [signupFormData, setSignupFormData] = useState({
    phone_number: "",
    full_name: "",
    role: "",
    quantity: "",
  });

  // Memoized fetch function to avoid recreating on every render
  const fetchActiveCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get("/leadgeneration/get-active-campaigns");
      setCampaigns(response.data.data);
    } catch (err) {
      console.error("Error fetching active campaigns:", err);
      showAlert(t("Failed to fetch active campaigns"), "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchActiveCampaigns();
  }, []);

  const handleViewDocument = (documentUrl) => {
    if (documentUrl) {
      window.open(documentUrl, "_blank");
    }
  };

  // Toggle expanded card
  const handleCardClick = (campaignId) => {
    setExpandedCampaignId((prevId) =>
      prevId === campaignId ? null : campaignId
    );
  };

  // Open signup dialog
  const handleOpenSignupDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setOpenSignupDialog(true);
    // Reset form data
    setSignupFormData({
      phone_number: "",
      full_name: "",
      role: "",
      quantity: "",
    });
  };

  // Handle input changes in signup dialog
  const handleSignupInputChange = (e) => {
    const { name, value } = e.target;
    setSignupFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle campaign signup from dialog
  const handleCampaignSignup = async () => {
    const errors = {};
    if (
      !signupFormData.phone_number ||
      signupFormData.phone_number.length != 10
    )
      errors.phone_number = "Phone number is required";
    if (!signupFormData.full_name) errors.full_name = "Full name is required";
    if (!signupFormData.role) errors.role = "Role is required";

    if (Object.keys(errors).length > 0) {
      showAlert("error", "Please fill in all required fields");
      return;
    }

    try {
      setSignupLoading((prev) => ({ ...prev, [selectedCampaign.id]: true }));

      await api.post(
        `/leadgeneration/campaigns/${selectedCampaign.id}/signup/`,
        signupFormData
      );

      await fetchActiveCampaigns();

      showAlert("success", "Successfully signed up for the campaign");
      setOpenSignupDialog(false);
    } catch (err) {
      const errorMessage =
        err.response?.data.error || t("Failed to signup for campaign");
      showAlert("error", errorMessage);
    } finally {
      setSignupLoading((prev) => ({ ...prev, [selectedCampaign.id]: false }));
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Existing campaigns grid */}
      <Grid container mt={1}>
        {campaigns.length === 0 ? (
          <Container
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80vh",
              textAlign: "center",
            }}
          >
            <Typography>{t("No active campaigns found.")}</Typography>
          </Container>
        ) : (
          campaigns.map((campaign) => (
            <UserCampaignCard
              key={campaign.id}
              campaign={campaign}
              isExpanded={expandedCampaignId === campaign.id}
              onCardClick={handleCardClick}
              onViewDocument={handleViewDocument}
              onSignUp={handleOpenSignupDialog}
              signupLoading={signupLoading[campaign.id]}
            />
          ))
        )}
      </Grid>

      {/* Signup Dialog */}
      <Dialog
        open={openSignupDialog}
        onClose={() => setOpenSignupDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("Sign Up for Campaign")}
          {selectedCampaign && `: ${selectedCampaign.title}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone_number"
                value={signupFormData.phone_number}
                onChange={handleSignupInputChange}
                required
                type="tel"
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                }}
                onKeyDown={handleMobileKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+91</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                name="full_name"
                value={signupFormData.full_name}
                onChange={handleSignupInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={signupFormData.role}
                  label="Role"
                  onChange={handleSignupInputChange}
                >
                  <MenuItem value="FARMER">Farmer</MenuItem>
                  <MenuItem value="FPO">FPO</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quantity (Optional)"
                name="quantity"
                type="number"
                value={signupFormData.quantity}
                onChange={handleSignupInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <GenericButton
            onClick={() => setOpenSignupDialog(false)}
            backgroundColor="#FF5722"
            fontColor="#fff"
            padding="10px 0"
          >
            {t("Cancel")}
          </GenericButton>
          <GenericButton
            onClick={handleCampaignSignup}
            backgroundColor="#4CAF50"
            fontColor="#fff"
            padding="10px 0"
            disabled={signupLoading[selectedCampaign?.id]}
          >
            {signupLoading[selectedCampaign?.id]
              ? t("Signing Up...")
              : t("Sign Up")}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActiveCampaigns;
